import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { 
  Upload, 
  FileUp, 
  Download, 
  AlertCircle, 
  CheckCircle,
  Settings,
  Users,
  LinkIcon,
  Plus,
  Copy,
  Check,
  FileSpreadsheet,
  Database,
  ArrowRightLeft,
  Loader2,
  FileText
} from 'lucide-react';
import GoogleSheetsIntegration from './GoogleSheetsIntegration';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IntegrationPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const { tt } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [activeTab, setActiveTab] = useState('file');
  const [apiSettings, setApiSettings] = useState({
    api_key: '',
    api_url: '',
    webhook_token: ''
  });
  const [apiConnectionResult, setApiConnectionResult] = useState(null);
  const [affiliateTokens, setAffiliateTokens] = useState([]);
  const [newTokenName, setNewTokenName] = useState('');
  const [copiedTokenId, setCopiedTokenId] = useState(null);
  const [editingWhitelistId, setEditingWhitelistId] = useState(null);
  const [whitelistInput, setWhitelistInput] = useState('');
  const [exportStats, setExportStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [remoteUrl, setRemoteUrl] = useState('');
  const [remoteEmail, setRemoteEmail] = useState('');
  const [remotePassword, setRemotePassword] = useState('');
  const [loadingStats, setLoadingStats] = useState(false);
  const [importingBackup, setImportingBackup] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [backupFile, setBackupFile] = useState(null);
  const [importMasterPw, setImportMasterPw] = useState('');
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
      setUploadResult(null);
    } else {
      alert(tt('Пожалуйста, выберите CSV или Excel файл'));
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(`${API}/upload-clients`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploadResult({
        success: true,
        message: response.data.message,
        details: {
          clients_created: response.data.clients_created,
          duplicates_found: response.data.duplicates_found,
          skipped_incomplete: response.data.skipped_incomplete,
          total_processed: response.data.total_processed,
          campaign: response.data.details?.campaign,
          skip_reasons: response.data.skip_reasons || {},
          skip_reason_summary: response.data.skip_reason_summary || [],
          skipped_samples: response.data.skipped_samples || [],
        }
      });
      setSelectedFile(null);
    } catch (error) {
      setUploadResult({
        success: false,
        message: error.response?.data?.detail || tt('Ошибка при загрузке файла')
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Динамический импорт xlsx только когда нужно
    import('xlsx').then((XLSX) => {
      // Создаем данные для EXCEL файла
      const data = [
        ['ФИО', 'Email', 'Телефон', 'Страна', 'Примечание'],
        ['Иван Иванов', 'ivan@example.com', '+79001234567', 'Россия', 'Заинтересован в торговле'],
        ['Мария Петрова', 'maria@example.com', '+79002345678', 'Казахстан', 'Запросил обратный звонок'],
        ['John Smith', 'john@example.com', '+79003456789', 'USA', 'VIP клиент']
      ];

      // Создаем worksheet и workbook
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Клиенты');

      // Скачиваем EXCEL файл
      XLSX.writeFile(wb, 'clients_template.xlsx');
    }).catch(err => {
      console.error('Ошибка загрузки xlsx библиотеки:', err);
      // Fallback к CSV если произошла ошибка
      const csvContent = "ФИО,Email,Телефон,Страна,Примечание\nИван Иванов,ivan@example.com,+79001234567,Россия,Заинтересован в торговле\nМария Петрова,maria@example.com,+79002345678,Казахстан,Запросил обратный звонок";
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "clients_template.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const testApiConnection = async () => {
    if (!apiSettings.api_key || !apiSettings.api_url) {
      setApiConnectionResult({
        success: false,
        message: tt('API ключ и URL обязательны для заполнения')
      });
      return;
    }

    try {
      const response = await axios.get(apiSettings.api_url, {
        headers: {
          'Authorization': `Bearer ${apiSettings.api_key}`,
          'Content-Type': 'application/json'
        }
      });

      setApiConnectionResult({
        success: true,
        message: tt('Соединение с API успешно установлено'),
        data: response.data
      });
    } catch (error) {
      setApiConnectionResult({
        success: false,
        message: `${tt('Ошибка соединения: ')}${error.response?.data?.message || error.message}`
      });
    }
  };

  const fetchAffiliateTokens = async () => {
    try {
      const response = await axios.get(`${API}/affiliate-tokens`);
      setAffiliateTokens(response.data);
    } catch (error) {
      console.error('Error fetching affiliate tokens:', error);
    }
  };

  const createAffiliateToken = async () => {
    if (!newTokenName.trim()) return;
    
    try {
      const response = await axios.post(`${API}/affiliate-tokens?name=${encodeURIComponent(newTokenName.trim())}`);
      setNewTokenName('');
      fetchAffiliateTokens();
      alert(tt('Токен создан успешно!'));
    } catch (error) {
      alert(tt('Ошибка создания токена: ') + (error.response?.data?.detail || error.message));
    }
  };

  const deleteAffiliateToken = async (tokenId) => {
    if (!window.confirm(tt('Вы уверены, что хотите удалить этот токен?'))) return;
    
    try {
      await axios.delete(`${API}/affiliate-tokens/${tokenId}`);
      fetchAffiliateTokens();
      alert(tt('Токен удален успешно!'));
    } catch (error) {
      alert(tt('Ошибка удаления токена: ') + (error.response?.data?.detail || error.message));
    }
  };

  const copyTokenToClipboard = async (tokenValue, tokenId) => {
    try {
      await navigator.clipboard.writeText(tokenValue);
      setCopiedTokenId(tokenId);
      setTimeout(() => setCopiedTokenId(null), 2000); // Reset after 2 seconds
    } catch (error) {
      console.error('Failed to copy token:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = tokenValue;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedTokenId(tokenId);
        setTimeout(() => setCopiedTokenId(null), 2000);
      } catch (err) {
        alert(tt('Не удалось скопировать токен'));
      }
      document.body.removeChild(textArea);
    }
  };

  const editWhitelist = (tokenId, currentWhitelist) => {
    setEditingWhitelistId(tokenId);
    setWhitelistInput(currentWhitelist ? currentWhitelist.join('\n') : '');
  };

  const saveWhitelist = async (tokenId) => {
    try {
      const ipAddresses = whitelistInput
        .split('\n')
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);
      
      await axios.put(`${API}/affiliate-tokens/${tokenId}/whitelist`, ipAddresses);
      setEditingWhitelistId(null);
      setWhitelistInput('');
      fetchAffiliateTokens();
      alert(tt('IP whitelist обновлен успешно!'));
    } catch (error) {
      alert(tt('Ошибка обновления whitelist: ') + (error.response?.data?.detail || error.message));
    }
  };

  const cancelEditWhitelist = () => {
    setEditingWhitelistId(null);
    setWhitelistInput('');
  };

  const fetchExportStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API}/admin/export-stats`);
      setExportStats(response.data);
    } catch (error) {
      console.error('Error fetching export stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleImportBackup = async () => {
    if (!backupFile) {
      alert(tt('Выберите файл бэкапа'));
      return;
    }
    if (!window.confirm(tt('ВНИМАНИЕ! Все текущие данные будут заменены данными из бэкапа. Продолжить?'))) return;
    setImportingBackup(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', backupFile);
      fd.append('replace', 'true');
      if (importMasterPw) fd.append('master_password', importMasterPw);
      const response = await axios.post(`${API}/admin/import-all-data`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000,
      });
      setImportResult({ status: 'success', ...response.data });
      setBackupFile(null);
      setImportMasterPw('');
      // refresh export stats
      try {
        const statsResp = await axios.get(`${API}/admin/export-stats`);
        setExportStats(statsResp.data);
      } catch {}
    } catch (error) {
      setImportResult({
        status: 'error',
        message: error.response?.data?.detail || error.message || tt('Ошибка импорта'),
      });
    } finally {
      setImportingBackup(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/export-all-data`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.setAttribute('download', `forex_crm_export_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(tt('Ошибка экспорта: ') + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const handleMigrateFromRemote = async () => {
    if (!remoteUrl || !remoteEmail || !remotePassword) {
      alert(tt('Заполните все поля'));
      return;
    }
    if (!window.confirm(`${tt('Перенести ВСЕ данные с')} ${remoteUrl} ${tt('на этот сервер? Текущие данные будут заменены.')}`)) return;

    setMigrating(true);
    setMigrateResult(null);
    try {
      // Start background migration
      await axios.post(`${API}/admin/migrate-from-remote`, {
        remote_url: remoteUrl,
        email: remoteEmail,
        password: remotePassword
      });

      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const statusResp = await axios.get(`${API}/admin/migrate-status`);
          const status = statusResp.data;
          setMigrateResult({ status: 'progress', message: status.progress || tt('Выполняется...') });

          if (!status.running && status.result) {
            clearInterval(pollInterval);
            setMigrateResult(status.result);
            setMigrating(false);
          }
        } catch {
          // ignore poll errors
        }
      }, 2000);
    } catch (error) {
      setMigrateResult({
        status: 'error',
        message: error.response?.data?.detail || error.message
      });
      setMigrating(false);
    }
  };

  React.useEffect(() => {
    fetchAffiliateTokens();
  }, []);

  useEffect(() => {
    if (activeTab === 'migration') {
      fetchExportStats();
    }
  }, [activeTab]);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{tt('Интеграция и импорт')}</h1>
        <p className="text-gray-600">{tt('Настройка API интеграций и загрузка данных')}</p>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('file')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'file'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tt('Загрузка файлов')}
            </button>
            <button
              onClick={() => setActiveTab('api')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'api'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tt('API интеграция')}
            </button>
            <button
              onClick={() => setActiveTab('affiliates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'affiliates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tt('Аффилиаты')}
            </button>
            <button
              onClick={() => setActiveTab('google-sheets')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'google-sheets'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileSpreadsheet className="w-4 h-4 inline mr-1" />
              Google Sheets
            </button>
            <button
              onClick={() => setActiveTab('migration')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'migration'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="migration-tab"
            >
              <ArrowRightLeft className="w-4 h-4 inline mr-1" />
              {tt('Миграция данных')}
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'file' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 rounded-full mr-4">
                  <Upload className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{tt('Загрузка клиентов')}</h3>
                  <p className="text-gray-600">{tt('CSV или Excel файл')}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-800 font-medium">
                      {tt('Выберите файл')}
                    </span>
                    <span className="text-gray-600"> {tt('или перетащите сюда')}</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">{tt('CSV, XLS, XLSX до 10MB')}</p>
                </div>

                {selectedFile && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-green-800 font-medium">{selectedFile.name}</span>
                      </div>
                      <span className="text-green-600 text-sm">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleFileUpload}
                  disabled={!selectedFile || uploading}
                  className={`btn w-full ${
                    !selectedFile || uploading ? 'btn-secondary' : 'btn-success'
                  }`}
                  data-testid="upload-button"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {tt('Загрузка...')}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {tt('Загрузить файл')}
                    </>
                  )}
                </button>

                {uploadResult && (
                  <div className={`p-4 rounded-lg ${
                    uploadResult.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`} data-testid="upload-result">
                    <div className="flex items-center mb-3">
                      {uploadResult.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      )}
                      <p className={uploadResult.success ? 'text-green-800 font-medium' : 'text-red-800 font-medium'}>
                        {uploadResult.success ? tt('Загрузка завершена!') : tt('Ошибка загрузки')}
                      </p>
                    </div>
                    
                    {uploadResult.success && uploadResult.details && (
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white p-3 rounded border">
                            <div className="text-gray-600">{tt('Всего обработано')}</div>
                            <div className="text-lg font-semibold text-gray-900">{uploadResult.details.total_processed}</div>
                          </div>
                          <div className="bg-white p-3 rounded border">
                            <div className="text-gray-600">{tt('Создано клиентов')}</div>
                            <div className="text-lg font-semibold text-green-600">{uploadResult.details.clients_created}</div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white p-3 rounded border">
                            <div className="text-gray-600">{tt('Найдено дублей')}</div>
                            <div className="text-lg font-semibold text-orange-600">{uploadResult.details.duplicates_found}</div>
                          </div>
                          <div className="bg-white p-3 rounded border">
                            <div className="text-gray-600">{tt('Пропущено неполных')}</div>
                            <div className="text-lg font-semibold text-gray-600">{uploadResult.details.skipped_incomplete}</div>
                          </div>
                        </div>

                        {/* Detailed skip reason breakdown */}
                        {uploadResult.details.skip_reason_summary && uploadResult.details.skip_reason_summary.length > 0 && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded" data-testid="skip-reasons-summary">
                            <div className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center">
                              <AlertCircle className="w-3.5 h-3.5 mr-1" />
                              {tt('Почему пропущены записи:')}
                            </div>
                            <ul className="text-xs text-amber-900 list-disc ml-5 space-y-0.5">
                              {uploadResult.details.skip_reason_summary.map((s, i) => (
                                <li key={i}>{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Per-row skipped samples (up to 20) */}
                        {uploadResult.details.skipped_samples && uploadResult.details.skipped_samples.length > 0 && (
                          <details className="mt-3 p-3 bg-gray-50 border rounded" data-testid="skipped-samples">
                            <summary className="text-xs font-semibold text-gray-700 cursor-pointer hover:text-gray-900">
                              {tt('Показать примеры пропущенных строк')} ({uploadResult.details.skipped_samples.length})
                            </summary>
                            <div className="mt-2 max-h-64 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-100 text-gray-600">
                                  <tr>
                                    <th className="px-2 py-1 text-left">{tt('Строка')}</th>
                                    <th className="px-2 py-1 text-left">{tt('Причина')}</th>
                                    <th className="px-2 py-1 text-left">{tt('Отсутствуют поля / Данные')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {uploadResult.details.skipped_samples.map((s, i) => (
                                    <tr key={i} className="border-t border-gray-200">
                                      <td className="px-2 py-1 font-mono">{s.row}</td>
                                      <td className="px-2 py-1">
                                        {s.reason === 'missing_fields' && <span className="text-amber-700">{tt('Пустые поля')}</span>}
                                        {s.reason === 'invalid_phone' && <span className="text-red-700">{tt('Некорректный телефон')}</span>}
                                        {s.reason === 'exception' && <span className="text-red-700">{tt('Ошибка обработки')}</span>}
                                      </td>
                                      <td className="px-2 py-1 text-gray-700">
                                        {s.missing_fields && (
                                          <span className="font-mono text-red-600">{s.missing_fields.join(', ')}</span>
                                        )}
                                        {s.reason === 'invalid_phone' && (
                                          <span className="font-mono">&quot;{s.phone}&quot;</span>
                                        )}
                                        {s.reason === 'exception' && (
                                          <span className="text-red-600">{s.error}</span>
                                        )}
                                        {(s.name || s.email) && (
                                          <span className="ml-2 text-gray-500">
                                            {s.name && `${s.name} `}
                                            {s.email && `<${s.email}>`}
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <p className="text-[10px] text-gray-500 mt-2">
                              {tt('Показаны первые 20 пропущенных строк. Обязательные поля: ФИО, Email, Телефон, Страна.')}
                            </p>
                          </details>
                        )}
                        
                        {uploadResult.details.campaign && (
                          <div className="mt-3 p-2 bg-blue-50 rounded border">
                            <div className="text-xs text-blue-600">{tt('Компания:')} <strong>{uploadResult.details.campaign}</strong></div>
                            <div className="text-xs text-blue-600">{tt('Статус новых клиентов:')} <strong>{tt('Новый')}</strong></div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {!uploadResult.success && (
                      <p className="text-red-700 text-sm mt-2">{uploadResult.message}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">{tt('Шаблон для загрузки')}</h4>
              <p className="text-gray-600 mb-4">
                {tt('Скачайте шаблон EXCEL/CSV файла с примером данных:')}
              </p>
              <button
                onClick={downloadTemplate}
                className="btn btn-secondary w-full mb-4"
                data-testid="download-template"
              >
                <Download className="w-4 h-4 mr-2" />
                {tt('Скачать шаблон EXCEL')}
              </button>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-2">{tt('Требования к файлу:')}</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• {tt('Обязательные колонки: ФИО, Email, Телефон, Страна')}</li>
                  <li>• {tt('Опциональная колонка: Примечание (заметки о клиенте)')}</li>
                  <li>• {tt('Поддерживаемые форматы: CSV, Excel (.xlsx, .xls)')}</li>
                  <li>• {tt('Кодировка UTF-8')}</li>
                  <li>• {tt('Максимальный размер файла: 10MB')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'api' ? (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-green-100 rounded-full mr-4">
                <Settings className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{tt('API интеграция')}</h3>
                <p className="text-gray-600">{tt('Подключение внешних сервисов по API')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">{tt('API ключ')}</label>
                  <input
                    type="text"
                    value={apiSettings.api_key}
                    onChange={(e) => setApiSettings({...apiSettings, api_key: e.target.value})}
                    className="form-input"
                    placeholder={tt('Ваш API ключ')}
                    data-testid="api-key-input"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">API URL</label>
                  <input
                    type="url"
                    value={apiSettings.api_url}
                    onChange={(e) => setApiSettings({...apiSettings, api_url: e.target.value})}
                    className="form-input"
                    placeholder="https://api.example.com"
                    data-testid="api-url-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Webhook Token</label>
                  <input
                    type="text"
                    value={apiSettings.webhook_token}
                    onChange={(e) => setApiSettings({...apiSettings, webhook_token: e.target.value})}
                    className="form-input"
                    placeholder={tt('Токен для webhook уведомлений')}
                    data-testid="webhook-token-input"
                  />
                </div>

                <button
                  onClick={testApiConnection}
                  className="btn btn-success w-full"
                  data-testid="test-api-connection"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  {tt('Тестировать подключение')}
                </button>

                {apiConnectionResult && (
                  <div className={`p-4 rounded-lg ${
                    apiConnectionResult.success 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {apiConnectionResult.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      )}
                      <span className={apiConnectionResult.success ? 'text-green-800' : 'text-red-800'}>
                        {apiConnectionResult.message}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4 text-sm text-gray-600">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h5 className="font-medium text-blue-900 mb-2">{tt('Поддерживаемые форматы API:')}</h5>
                  <ul className="list-disc list-inside space-y-1 text-blue-800">
                    <li>REST API {tt('с JSON ответами')}</li>
                    <li>{tt('Авторизация по Bearer Token')}</li>
                    <li>{tt('Webhook уведомления')}</li>
                    <li>{tt('Пагинация данных')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'affiliates' ? (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-purple-100 rounded-full mr-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{tt('Управление аффилиатами')}</h3>
                <p className="text-gray-600">{tt('API токены для партнеров и аффилиатов')}</p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">{tt('Создать новый токен')}</h4>
              <div className="flex space-x-4">
                <input
                  type="text"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  className="form-input flex-1"
                  placeholder={tt('Название партнера/аффилиата')}
                />
                <button
                  onClick={createAffiliateToken}
                  className="btn btn-primary"
                  disabled={!newTokenName.trim()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {tt('Создать токен')}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900">{tt('Активные токены')}</h4>
              {affiliateTokens.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>{tt('Токенов пока нет')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{tt('Название')}</th>
                        <th>{tt('Токен')}</th>
                        <th>IP Whitelist</th>
                        <th>{tt('Создан')}</th>
                        <th>{tt('Последнее использование')}</th>
                        <th>{tt('Действия')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliateTokens.map((token) => (
                        <React.Fragment key={token.id}>
                          <tr>
                            <td className="font-medium">{token.name}</td>
                            <td>
                              <div className="flex items-center space-x-2">
                                <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                                  {token.token.substring(0, 8)}...{token.token.substring(token.token.length - 4)}
                                </code>
                                <button
                                  onClick={() => copyTokenToClipboard(token.token, token.id)}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  title={tt('Скопировать токен')}
                                >
                                  {copiedTokenId === token.id ? (
                                    <Check className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-gray-600" />
                                  )}
                                </button>
                              </div>
                            </td>
                            <td>
                              {editingWhitelistId === token.id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={whitelistInput}
                                    onChange={(e) => setWhitelistInput(e.target.value)}
                                    placeholder={tt('Введите IP адреса или CIDR (по одному на строку)&#10;Пример:&#10;192.168.1.1&#10;10.0.0.0/24')}
                                    className="form-input w-full text-sm"
                                    rows="3"
                                  />
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => saveWhitelist(token.id)}
                                      className="btn btn-sm btn-primary"
                                    >
                                      {tt('Сохранить')}
                                    </button>
                                    <button
                                      onClick={cancelEditWhitelist}
                                      className="btn btn-sm btn-secondary"
                                    >
                                      {tt('Отмена')}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  {token.ip_whitelist && token.ip_whitelist.length > 0 ? (
                                    <div className="text-sm">
                                      <span className="text-green-600 font-medium">
                                        {token.ip_whitelist.length} IP
                                      </span>
                                      <button
                                        onClick={() => editWhitelist(token.id, token.ip_whitelist)}
                                        className="ml-2 text-blue-600 hover:text-blue-800"
                                      >
                                        {tt('Редактировать')}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="text-sm">
                                      <span className="text-gray-500">{tt('Доступ с любого IP')}</span>
                                      <button
                                        onClick={() => editWhitelist(token.id, [])}
                                        className="ml-2 text-blue-600 hover:text-blue-800"
                                      >
                                        {tt('Настроить')}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td>{new Date(token.created_at).toLocaleDateString()}</td>
                            <td>{token.last_used ? new Date(token.last_used).toLocaleDateString() : tt('Не использовался')}</td>
                            <td>
                              <button
                                onClick={() => deleteAffiliateToken(token.id)}
                                className="btn btn-danger btn-sm"
                              >
                                {tt('Удалить')}
                              </button>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <h5 className="font-medium text-blue-900 mb-2">{tt('IP Whitelist (Контроль доступа):')}</h5>
              <div className="text-blue-800 text-sm space-y-2">
                <p>• <strong>{tt('Пустой whitelist:')}</strong> {tt('Доступ с любого IP адреса (по умолчанию)')}</p>
                <p>• <strong>{tt('С IP адресами:')}</strong> {tt('Доступ только с указанных IP')}</p>
                <p>• <strong>{tt('Поддерживаемые форматы:')}</strong></p>
                <ul className="list-disc list-inside ml-4">
                  <li>{tt('Отдельные IP:')} <code className="bg-white px-1 rounded">192.168.1.1</code></li>
                  <li>{tt('CIDR диапазоны:')} <code className="bg-white px-1 rounded">192.168.1.0/24</code></li>
                  <li>{tt('Несколько IP/диапазонов (по одному на строку)')}</li>
                </ul>
                <p className="mt-2 text-xs">⚠️ {tt('При блокировке IP аффилейт получит ошибку 401 "Доступ запрещен"')}</p>
              </div>
            </div>

            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <h5 className="font-medium text-blue-900 mb-2">{tt('API эндпоинты для аффилиатов:')}</h5>
              
              {/* Full URL Examples */}
              <div className="mb-4 pb-3 border-b border-blue-200">
                <p className="font-medium text-xs text-blue-900 mb-2">{tt('Полные URL для интеграции:')}</p>
                <div className="space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-xs font-medium text-gray-700">{tt('Создание клиента:')}</p>
                    <code className="text-xs text-blue-700 break-all">
                      {window.location.origin}/api/external/clients?token=YOUR_TOKEN
                    </code>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-xs font-medium text-gray-700">{tt('Получение клиентов:')}</p>
                    <code className="text-xs text-blue-700 break-all">
                      {window.location.origin}/api/external/clients?token=YOUR_TOKEN&limit=100&offset=0
                    </code>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2">{tt('URL автоматически адаптируется к вашему домену')}</p>
              </div>
              
              <div className="text-sm text-blue-800 space-y-3">
                <div>
                  <strong>POST</strong> <code className="bg-white px-1 rounded">/api/external/clients?token=YOUR_TOKEN</code>
                  <p className="text-xs text-blue-600 ml-4">{tt('Создание клиента от аффилиата')}</p>
                </div>
                <div>
                  <strong>GET</strong> <code className="bg-white px-1 rounded">/api/external/clients?token=YOUR_TOKEN&limit=100&offset=0</code>
                  <p className="text-xs text-blue-600 ml-4">{tt('Получение списка клиентов аффилиата (для отслеживания изменений статуса и FTD)')}</p>
                </div>
                
                <div className="mt-4 pt-3 border-t border-blue-200">
                  <p className="font-medium mb-2">{tt('Возможные ответы (POST):')}</p>
                  <ul className="space-y-2 text-xs">
                    <li className="bg-green-50 p-2 rounded">
                      ✅ <code className="bg-white px-1 rounded">201</code> + <code className="bg-white px-1 rounded">{`{"status": "created", "client_id": 123, "client": {...}}`}</code>
                      <p className="ml-4 mt-1 text-green-700">{tt('Клиент успешно создан. Возвращает: email, phone, country, status, ftd, note')}</p>
                    </li>
                    <li className="bg-yellow-50 p-2 rounded">
                      ⚠️ <code className="bg-white px-1 rounded">400</code> + <code className="bg-white px-1 rounded">{`{"detail": {"status": "duplicate", "client_id": 456, "client": {...}}}`}</code>
                      <p className="ml-4 mt-1 text-yellow-700">{tt('Клиент уже существует (поиск по email — без учёта регистра — или по последним 9 цифрам телефона). Новый клиент НЕ создан. Тело ответа находится внутри поля "detail" (стандарт FastAPI).')}</p>
                    </li>
                    <li className="bg-red-50 p-2 rounded">
                      ❌ <code className="bg-white px-1 rounded">401</code> - {tt('невалидный/неактивный токен ИЛИ IP заблокирован')}
                    </li>
                    <li className="bg-red-50 p-2 rounded">
                      ❌ <code className="bg-white px-1 rounded">400</code> - {tt('неверные данные в запросе (либо дубликат — см. выше)')}
                    </li>
                    <li className="bg-red-50 p-2 rounded">
                      ❌ <code className="bg-white px-1 rounded">404</code> - {tt('неправильный URL (проверьте: должен быть')} <code className="bg-white px-1 rounded">/api/external/clients</code>)
                    </li>
                  </ul>
                </div>
                
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="font-medium mb-2 text-xs">📦 {tt('Данные в ответе при успехе (POST):')}</p>
                  <ul className="space-y-1 text-xs ml-4">
                    <li>• <strong>email</strong> - {tt('email клиента (для подтверждения)')}</li>
                    <li>• <strong>phone</strong> - {tt('телефон клиента в нормализованном формате')}</li>
                    <li>• <strong>status</strong> - {tt('статус клиента (new, potential, active, и т.д.)')}</li>
                    <li>• <strong>ftd</strong> - {tt('есть ли FTD (true/false)')}</li>
                  </ul>
                </div>
                
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="font-medium mb-2 text-xs">📋 {tt('Ответ GET /api/external/clients:')}</p>
                  <pre className="bg-white p-2 rounded text-xs overflow-x-auto">
{`{
  "affiliate": "${tt('Имя токена')}",
  "total_clients": 3,
  "clients": [
    {
      "client_id": 123,
      "name": "${tt('Иван Петров')}",
      "email": "ivan@example.com",
      "phone": "+79001234567",
      "status": "ftd",
      "ftd": true,
      "created_at": "2025-10-14T10:30:00"
    }
  ]
}`}
                  </pre>
                  <p className="text-xs text-blue-700 mt-2">💡 {tt('Используйте GET для периодической проверки изменений статуса и FTD ваших клиентов')}</p>
                </div>
                
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="font-medium mb-1 text-xs">{tt('Важно:')}</p>
                  <ul className="space-y-1 text-xs">
                    <li>{tt('URL должен начинаться с')} <code className="bg-white px-1 rounded">/api</code></li>
                    <li>{tt('Токен передается как query параметр:')} <code className="bg-white px-1 rounded">?token=...</code></li>
                    <li>Header: <code className="bg-white px-1 rounded">Content-Type: application/json</code></li>
                  </ul>
                </div>
                
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="font-medium mb-2 text-xs">{tt('Пример JSON для тестирования (POST):')}</p>
                  <pre className="bg-white p-2 rounded text-xs overflow-x-auto">
{`{
  "name": "${tt('Иван Петров')}",
  "email": "ivan@example.com",
  "phone": "+79001234567",
  "country": "Russia",
  "note": "${tt('Интересуется Forex')}",
  "campaign_name": "Forex Trading",
  "ad_name": "${tt('Начни торговать сегодня')}",
  "utm_source": "facebook",
  "utm_campaign": "q4_2024"
}`}
                  </pre>
                </div>

                <div className="mt-4 pt-3 border-t border-blue-200">
                  <p className="font-medium mb-2 text-xs">{tt('Скачать инструкцию для аффилейта:')}</p>
                  <p className="text-xs text-gray-500 mb-2">{tt('PDF с эндпоинтами, примерами и описанием — отправьте аффилейту (токен отправляется отдельно)')}</p>
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('token');
                      fetch(`${API}/affiliate-guide/pdf`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      }).then(r => r.blob()).then(blob => {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'affiliate_integration_guide.pdf';
                        a.click();
                        URL.revokeObjectURL(url);
                      });
                    }}
                    data-testid="download-affiliate-pdf"
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-sm transition-colors"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-700">{tt('Скачать PDF инструкцию')}</span>
                    <Download className="w-3.5 h-3.5 text-blue-400 ml-auto" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'google-sheets' ? (
        <GoogleSheetsIntegration />
      ) : activeTab === 'migration' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="migration-panel">
          {/* PULL FROM REMOTE */}
          <div className="card p-6">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-orange-100 rounded-full mr-4">
                <ArrowRightLeft className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{tt('Перенести данные с другого сервера')}</h3>
                <p className="text-sm text-gray-500">{tt('Введите адрес старого домена — данные перенесутся автоматически')}</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tt('URL старого домена')}</label>
                <input
                  type="url"
                  placeholder="https://investeds.vip"
                  value={remoteUrl}
                  onChange={(e) => setRemoteUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="remote-url-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tt('Email суперадмина')}</label>
                <input
                  type="email"
                  placeholder="admin@forex.com"
                  value={remoteEmail}
                  onChange={(e) => setRemoteEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="remote-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{tt('Пароль')}</label>
                <input
                  type="password"
                  placeholder={tt('Пароль суперадмина')}
                  value={remotePassword}
                  onChange={(e) => setRemotePassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid="remote-password-input"
                />
              </div>
            </div>

            <button
              onClick={handleMigrateFromRemote}
              disabled={migrating || !remoteUrl || !remoteEmail || !remotePassword}
              className="w-full py-3 px-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center font-medium"
              data-testid="migrate-btn"
            >
              {migrating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {tt('Переносим данные...')}
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-5 h-5 mr-2" />
                  {tt('Перенести все данные')}
                </>
              )}
            </button>

            {migrateResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                migrateResult.status === 'success' ? 'bg-green-50 border border-green-200' : 
                migrateResult.status === 'progress' ? 'bg-blue-50 border border-blue-200' :
                'bg-red-50 border border-red-200'
              }`} data-testid="migrate-result">
                <div className="flex items-center mb-2">
                  {migrateResult.status === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  ) : migrateResult.status === 'progress' ? (
                    <Loader2 className="w-5 h-5 text-blue-600 mr-2 animate-spin" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  )}
                  <span className={`font-medium ${
                    migrateResult.status === 'success' ? 'text-green-800' : 
                    migrateResult.status === 'progress' ? 'text-blue-800' :
                    'text-red-800'
                  }`}>
                    {migrateResult.message}
                  </span>
                </div>
                {migrateResult.results && (
                  <div className="mt-2 space-y-1">
                    {Object.entries(migrateResult.results).map(([name, info]) => (
                      <div key={name} className="flex justify-between text-xs text-gray-600">
                        <span>{name}</span>
                        <span className="font-mono">
                          {info.status === 'imported' ? `+${info.inserted_new}` : info.reason || info.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {migrateResult.note && (
                  <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                    {migrateResult.note}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">
                {tt('Сервер подключится к старому домену, заберёт все данные (клиенты, депозиты, сделки, пользователи и т.д.) и перенесёт их сюда.')}
              </p>
            </div>
          </div>

          {/* IMPORT BACKUP FILE (offline migration) */}
          <div className="card p-6" data-testid="import-backup-panel">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-purple-100 rounded-full mr-4">
                <Upload className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{tt('Загрузить бэкап')}</h3>
                <p className="text-sm text-gray-500">{tt('Восстановить ВСЕ данные из JSON-файла бэкапа')}</p>
              </div>
            </div>

            <div className="space-y-4 mb-4">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">{tt('Файл бэкапа (.json)')}</span>
                <input
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => { setBackupFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
                  data-testid="backup-file-input"
                />
              </label>
              {backupFile && (
                <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded">
                  <FileText className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  {backupFile.name} <span className="text-gray-400">({(backupFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
              )}

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">{tt('Мастер-пароль')}</span>
                <input
                  type="password"
                  value={importMasterPw}
                  onChange={(e) => setImportMasterPw(e.target.value)}
                  placeholder={tt('Требуется для подтверждения')}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="import-master-pw-input"
                  autoComplete="new-password"
                />
              </label>
            </div>

            <button
              onClick={handleImportBackup}
              disabled={importingBackup || !backupFile}
              className="w-full py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center font-medium"
              data-testid="import-backup-btn"
            >
              {importingBackup ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {tt('Восстанавливаем данные...')}
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  {tt('Восстановить из бэкапа')}
                </>
              )}
            </button>

            {importResult && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${
                importResult.status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`} data-testid="import-result">
                <div className="flex items-center mb-2">
                  {importResult.status === 'success' ? (
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  )}
                  <span className={`font-medium ${
                    importResult.status === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>{importResult.message || importResult.status}</span>
                </div>
                {importResult.collections && (
                  <div className="space-y-0.5 max-h-40 overflow-y-auto">
                    {Object.entries(importResult.collections).map(([name, info]) => (
                      <div key={name} className="flex justify-between text-xs text-gray-600">
                        <span>{name}</span>
                        <span className="font-mono">
                          {info.status === 'imported' ? `+${info.inserted}` : info.reason || info.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 p-3 bg-amber-50 rounded-lg">
              <p className="text-xs text-amber-800">
                <AlertCircle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                <b>{tt('Внимание:')}</b> {tt('эта операция полностью заменит все текущие данные системы. Используйте при переезде на новый домен — скачайте бэкап на старом домене (раздел «Главная» → «Скачать бэкап БД») и загрузите его сюда.')}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default IntegrationPage;